// CardSectionsEditor — director config for a funnel's custom deal-card sections.
// Rendered inside the funnel editor (under the stage list). The director adds
// named sections (text / files), reorders, renames, and removes them; saving
// PUTs the whole list (the backend replaces the funnel template atomically).
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { Button, Icon, SectionHeader, Skel, useToast, t } from '../../design'
import {
  fetchCardSections,
  saveCardSections,
  type CardSectionType,
} from '../../api/cardSections'

type Row = { key: string; label: string; type: CardSectionType }

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 11px', borderRadius: 10,
  border: '1px solid var(--border)', background: 'var(--surface)',
  color: 'var(--text-1)', fontSize: 14,
}

function TypeChip({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="t-caption"
      style={{
        padding: '5px 10px', borderRadius: 8, cursor: 'pointer',
        border: `1px solid ${active ? 'var(--brand-700)' : 'var(--border)'}`,
        background: active ? 'var(--brand-50, var(--surface-2))' : 'transparent',
        color: active ? 'var(--brand-700)' : 'var(--text-2)',
        fontWeight: active ? 600 : 400,
      }}
    >
      {children}
    </button>
  )
}

export function CardSectionsEditor({ pipelineId }: { pipelineId: string }) {
  const qc = useQueryClient()
  const toast = useToast()
  const { data, isLoading } = useQuery({
    queryKey: ['card-sections', pipelineId],
    queryFn: () => fetchCardSections(pipelineId),
  })

  // Local draft; null until the director touches anything (then it's authoritative).
  const [draft, setDraft] = useState<Row[] | null>(null)
  const rows: Row[] = draft
    ?? (data?.sections ?? []).map((s) => ({ key: s.key, label: s.label, type: s.type }))

  const saveMut = useMutation({
    mutationFn: () => saveCardSections(
      pipelineId,
      rows.map((r) => ({ key: r.key || undefined, label: r.label.trim(), type: r.type })),
    ),
    onSuccess: (r) => {
      setDraft(r.sections.map((s) => ({ key: s.key, label: s.label, type: s.type })))
      qc.setQueryData(['card-sections', pipelineId], r)
      toast(t('csec.saved'))
    },
    onError: () => toast(t('csec.saveErr')),
  })

  const set = (next: Row[]) => setDraft(next)
  const update = (i: number, patch: Partial<Row>) =>
    set(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))
  const add = () => set([...rows, { key: '', label: '', type: 'text' }])
  const remove = (i: number) => set(rows.filter((_, idx) => idx !== i))
  const move = (i: number, dir: -1 | 1) => {
    const j = i + dir
    if (j < 0 || j >= rows.length) return
    const next = [...rows]
    ;[next[i], next[j]] = [next[j], next[i]]
    set(next)
  }

  const hasEmpty = rows.some((r) => !r.label.trim())

  return (
    <div style={{ marginTop: 8 }}>
      <SectionHeader>{t('csec.title')}</SectionHeader>
      <div className="t-caption dim" style={{ marginBottom: 8 }}>{t('csec.hint')}</div>

      {isLoading ? (
        <Skel w="100%" h={64} r={12} />
      ) : (
        <div className="col gap-2">
          {rows.map((r, i) => (
            <div key={r.key || `new-${i}`} className="card card-16 col gap-2">
              <input
                style={inputStyle}
                value={r.label}
                placeholder={t('csec.namePh')}
                onChange={(e) => update(i, { label: e.target.value })}
              />
              <div className="row between middle" style={{ gap: 8 }}>
                <div className="row gap-1">
                  <TypeChip active={r.type === 'text'} onClick={() => update(i, { type: 'text' })}>
                    {t('csec.typeText')}
                  </TypeChip>
                  <TypeChip active={r.type === 'files'} onClick={() => update(i, { type: 'files' })}>
                    {t('csec.typeFiles')}
                  </TypeChip>
                </div>
                <div className="row gap-1">
                  <IconBtn disabled={i === 0} onClick={() => move(i, -1)} label={t('pip.moveUp')}>
                    <Icon.ArrowUp size={16} sw={2} />
                  </IconBtn>
                  <IconBtn disabled={i === rows.length - 1} onClick={() => move(i, 1)} label={t('pip.moveDown')}>
                    <Icon.ArrowDown size={16} sw={2} />
                  </IconBtn>
                  <IconBtn onClick={() => remove(i)} label={t('csec.delete')} danger>
                    <Icon.Trash size={16} sw={2} />
                  </IconBtn>
                </div>
              </div>
            </div>
          ))}
          {rows.length === 0 && (
            <div className="t-caption dim" style={{ padding: '2px 2px 4px' }}>{t('csec.empty')}</div>
          )}
        </div>
      )}

      <div className="row gap-2" style={{ marginTop: 10 }}>
        <Button variant="secondary" sm icon={<Icon.Plus size={15} sw={2} />} onClick={add}>
          {t('csec.add')}
        </Button>
        <Button
          sm
          disabled={hasEmpty || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? t('pip.saving') : t('pip.save')}
        </Button>
      </div>
      {hasEmpty && (
        <div className="t-caption" style={{ color: 'var(--warning)', marginTop: 6 }}>
          {t('csec.emptyLabelErr')}
        </div>
      )}
    </div>
  )
}

function IconBtn({ children, onClick, disabled, label, danger }: {
  children: React.ReactNode; onClick: () => void
  disabled?: boolean; label: string; danger?: boolean
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      style={{
        width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)',
        color: danger ? 'var(--danger)' : 'var(--text-2)',
        cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: 0,
      }}
    >
      {children}
    </button>
  )
}
